import { useState, useEffect } from "react";
import { Trophy, Medal, Star, Gift, Calendar, Crown, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Prize information
const PRIZES = {
  monthly: {
    first: "£25 Gift Card",
    second: "£15 Gift Card", 
    third: "£10 Gift Card"
  },
  yearly: {
    trophy: "Champion Trophy + £200 Gift Card",
    runnerUp: "Silver Medal + £100 Gift Card",
    third: "Bronze Medal + £50 Gift Card"
  }
};

// Get next draw date (last day of current month)
const getNextMonthlyDraw = () => {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return lastDay;
};

// Get days until year end
const getDaysUntilYearEnd = () => {
  const now = new Date();
  const yearEnd = new Date(now.getFullYear(), 11, 31);
  const diff = yearEnd - now;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export default function Leaderboard() {
  const [activeTab, setActiveTab] = useState("monthly");
  const [monthlyLeaderboard, setMonthlyLeaderboard] = useState([]);
  const [yearlyLeaderboard, setYearlyLeaderboard] = useState([]);
  const [allTimeLeaderboard, setAllTimeLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrizes, setShowPrizes] = useState(false);
  const [pointsConfig, setPointsConfig] = useState(null);

  useEffect(() => {
    const fetchLeaderboards = async () => {
      try {
        const [monthly, yearly, allTime, config] = await Promise.all([
          axios.get(`${API}/gamification/leaderboard/monthly`),
          axios.get(`${API}/gamification/leaderboard/yearly`),
          axios.get(`${API}/gamification/leaderboard/all-time`),
          axios.get(`${API}/gamification/points-config`)
        ]);
        
        setMonthlyLeaderboard(monthly.data);
        setYearlyLeaderboard(yearly.data);
        setAllTimeLeaderboard(allTime.data);
        setPointsConfig(config.data);
      } catch (error) {
        console.error("Failed to fetch leaderboards:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboards();
  }, []);

  const getLeaderboard = () => {
    switch (activeTab) {
      case "monthly": return monthlyLeaderboard;
      case "yearly": return yearlyLeaderboard;
      case "all-time": return allTimeLeaderboard;
      default: return monthlyLeaderboard;
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-gray-500">#{rank}</span>;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200";
    if (rank === 2) return "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200";
    if (rank === 3) return "bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200";
    return "bg-white border-gray-100";
  };

  const nextDraw = getNextMonthlyDraw();
  const daysUntilYearEnd = getDaysUntilYearEnd();

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Prize Info */}
      <div className="bg-gradient-to-br from-brand to-brand-dark rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">Contributor Leaderboard</h2>
              <p className="text-white/80 text-sm">Win prizes by contributing!</p>
            </div>
          </div>
          <button
            onClick={() => setShowPrizes(!showPrizes)}
            className="flex items-center gap-1 px-3 py-1.5 bg-white/20 rounded-lg text-sm font-medium hover:bg-white/30 transition-colors"
          >
            <Gift className="w-4 h-4" />
            Prizes
            {showPrizes ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        <AnimatePresence>
          {showPrizes && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/20">
                <div className="bg-white/10 rounded-xl p-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Monthly Prizes
                  </h3>
                  <ul className="text-sm space-y-1 text-white/90">
                    <li>🥇 1st Place: {PRIZES.monthly.first}</li>
                    <li>🥈 2nd Place: {PRIZES.monthly.second}</li>
                    <li>🥉 3rd Place: {PRIZES.monthly.third}</li>
                  </ul>
                  <p className="text-xs text-white/60 mt-2">
                    Next draw: {nextDraw.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                  </p>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Trophy className="w-4 h-4" /> Yearly Grand Prize
                  </h3>
                  <ul className="text-sm space-y-1 text-white/90">
                    <li>🏆 Champion: {PRIZES.yearly.trophy}</li>
                    <li>🥈 Runner-up: {PRIZES.yearly.runnerUp}</li>
                    <li>🥉 3rd Place: {PRIZES.yearly.third}</li>
                  </ul>
                  <p className="text-xs text-white/60 mt-2">
                    {daysUntilYearEnd} days until year-end showdown!
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Points Info */}
        {pointsConfig && (
          <div className="flex flex-wrap gap-3 mt-4">
            <span className="px-2 py-1 bg-white/20 rounded text-xs">
              📝 {pointsConfig.review_verified} pts/review
            </span>
            <span className="px-2 py-1 bg-white/20 rounded text-xs">
              📍 {pointsConfig.venue_verified} pts/venue
            </span>
            <span className="px-2 py-1 bg-white/20 rounded text-xs">
              ⭐ +{pointsConfig.venue_premium_upgrade || 50} bonus if venue goes premium
            </span>
          </div>
        )}
      </div>

      {/* Tab Selector */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
        {[
          { id: "monthly", label: "This Month" },
          { id: "yearly", label: "This Year" },
          { id: "all-time", label: "All Time" }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Leaderboard List */}
      <div className="space-y-2">
        {getLeaderboard().length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
            <Trophy className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No contributors yet this period</p>
            <p className="text-sm text-gray-400 mt-1">Be the first to earn points!</p>
          </div>
        ) : (
          getLeaderboard().map((entry, index) => (
            <motion.div
              key={entry.contributor_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`flex items-center gap-4 p-4 rounded-xl border ${getRankStyle(entry.rank)}`}
            >
              {/* Rank */}
              <div className="w-10 flex justify-center">
                {getRankIcon(entry.rank)}
              </div>

              {/* Avatar */}
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                {entry.avatar ? (
                  entry.avatar_type === "preset" ? (
                    <img src={entry.avatar} alt={entry.username} className="w-full h-full object-cover" />
                  ) : (
                    <img src={entry.avatar} alt={entry.username} className="w-full h-full object-cover" />
                  )
                ) : (
                  <span className="text-xl font-bold text-gray-400">
                    {entry.username?.[0]?.toUpperCase() || "?"}
                  </span>
                )}
              </div>

              {/* Name & Stats */}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{entry.username || "Anonymous"}</p>
                <p className="text-sm text-gray-500">
                  {activeTab === "monthly" && `${entry.monthly_points} pts this month`}
                  {activeTab === "yearly" && `${entry.yearly_points} pts this year`}
                  {activeTab === "all-time" && `${entry.total_points} total pts`}
                </p>
              </div>

              {/* Points Badge */}
              <div className="text-right">
                <div className={`text-lg font-bold ${
                  entry.rank === 1 ? "text-yellow-600" :
                  entry.rank === 2 ? "text-gray-500" :
                  entry.rank === 3 ? "text-amber-600" :
                  "text-brand"
                }`}>
                  {activeTab === "monthly" ? entry.monthly_points :
                   activeTab === "yearly" ? entry.yearly_points :
                   entry.total_points}
                </div>
                <div className="text-xs text-gray-400">points</div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 text-center">
        <Star className="w-8 h-8 mx-auto text-brand mb-2" />
        <h3 className="font-semibold text-gray-900 mb-1">Want to climb the leaderboard?</h3>
        <p className="text-sm text-gray-600 mb-4">
          Submit venues and write reviews to earn points!
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="/add-location"
            className="px-4 py-2 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
          >
            Add a Venue (+25 pts)
          </a>
          <a
            href="/browse"
            className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Write a Review (+10 pts)
          </a>
        </div>
      </div>
    </div>
  );
}
